import { ArrowRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackAffiliateClick } from "@/utils/gtmTracking";
import samblaLogo from "@/assets/sambla_stor.png";

const preloadSamblaAnalysis = () => {
  void import("@/pages/SamblaPage");
};

const HeroSection = () => {
  const { currentLanguage } = useLanguage();

  return (
    <section
      id="hero-section"
      className="relative overflow-hidden pt-20 pb-8 md:pt-24 md:pb-10 lg:pt-32 lg:pb-20"
      style={{ backgroundColor: "hsl(var(--primary-dark))" }}
    >
      <div className="container relative z-10">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px] lg:items-center lg:gap-16">
          <div className="max-w-3xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-white/75">
              Klarfinans
            </p>
            <h1 className="max-w-3xl text-3xl font-bold leading-tight text-white text-balance md:text-4xl lg:text-[3.5rem] lg:leading-[1.05] lg:tracking-tight">
              Jämför lån, bolån och företagslån – hitta bästa villkoren
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-white/90 md:text-lg lg:mt-6 lg:text-xl lg:leading-8">
              Jämför långivare, se villkor och gå vidare när du är redo.
            </p>

            {/* Desktop-only primary actions — fills the hero elegantly and gives
                immediate paths. The phone layout is intentionally left untouched. */}
            <div className="mt-8 hidden flex-wrap items-center gap-3 lg:flex">
              <Link
                to={`/${currentLanguage}/privatlan`}
                className="inline-flex min-h-[48px] items-center gap-2 rounded-md bg-white px-6 text-sm font-semibold text-primary-dark transition-colors hover:bg-white/90"
              >
                Jämför privatlån
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to={`/${currentLanguage}/bolan`}
                className="inline-flex min-h-[48px] items-center gap-2 rounded-md border border-white/30 px-6 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                Jämför bolån
              </Link>
            </div>

            <p className="mt-4 max-w-2xl text-xs leading-5 text-white/75 lg:mt-8">
              <strong>Att låna kostar pengar.</strong> Säkerställ att du kan betala tillbaka innan du ansöker.
            </p>
          </div>

          <aside className="rounded-lg border border-white/15 bg-white p-4 text-slate-950 shadow-md md:p-5 lg:rounded-xl lg:p-6 lg:shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">Partner i fokus</p>
                <img
                  src={samblaLogo}
                  alt="Sambla"
                  className="mt-2 h-7 w-auto object-contain lg:h-8"
                  width="120"
                  height="32"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>

            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              Sambla är en låneförmedlare. Ansökan sker hos Sambla, inte på Klarfinans.
            </p>

            <div className="mt-4 grid grid-cols-2 gap-3 border-y py-3">
              <div>
                <p className="text-xs text-muted-foreground">Långivare</p>
                <p className="mt-1 text-lg font-bold">40+</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Max belopp</p>
                <p className="mt-1 whitespace-nowrap text-lg font-bold">800 000 kr</p>
              </div>
            </div>

            <div className="mt-3 grid gap-2 text-sm">
              {["En ansökan", "En UC-kontroll", "Kostnadsfri jämförelse"].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-4 grid gap-2">
              <a
                href="https://go.adt284.net/t/t?a=1923302872&as=2024680921&t=2&tk=1"
                target="_blank"
                rel="sponsored nofollow noopener noreferrer"
                onClick={() =>
                  trackAffiliateClick({
                    provider: "Sambla",
                    pageType: "homepage",
                    section: "hero-card",
                  })
                }
                className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-dark"
              >
                Gå till Sambla
                <ExternalLink className="h-4 w-4" />
              </a>
              <Link
                to={`/${currentLanguage}/privatlan/sambla`}
                className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-primary hover:underline"
                onFocus={preloadSamblaAnalysis}
                onMouseEnter={preloadSamblaAnalysis}
                onTouchStart={preloadSamblaAnalysis}
              >
                Läs analysen
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
